import { useRef } from 'react';
import { saveToJSON, loadFromJSON } from '../utils/serializer';
import { sanitizeFileStem } from '../utils';
import type { DiagramType, SequenceParticipant, SequenceMessage, SequenceFragment, SequenceParticipantGroup, Node, Edge, Layer, NodeGroup, DiagramPage, Graph, CatalogComponent } from '../types';

type LoadedDiagram = ReturnType<typeof loadFromJSON>;
import { snapshotFromStore } from '../stores/undoStore';
import { useUIStore } from '../stores/uiStore';

declare global {
  interface Window {
    showSaveFilePicker?: (opts?: { suggestedName?: string; types?: Array<{ description: string; accept: Record<string, string[]> }> }) => Promise<FileSystemFileHandle>;
    showOpenFilePicker?: (opts?: { multiple?: boolean; types?: Array<{ description: string; accept: Record<string, string[]> }> }) => Promise<FileSystemFileHandle[]>;
  }
}

export interface PersistenceConfig {
  diagramType: DiagramType;
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  nodes: Node[];
  edges: Edge[];
  viewport: { x: number; y: number; scale: number };
  layers: Layer[];
  groups: NodeGroup[];
  pages: DiagramPage[];
  activePage: string;
  graphs?: Record<string, Graph>;
  activeGraphId?: string;
  catalog?: CatalogComponent[];
  setDiagramType: (t: DiagramType) => void;
  clearDiagram: () => void;
  loadDiagram: (data: LoadedDiagram) => void;
  setJsonError: (err: string | null) => void;
  jsonText: string;
  suggestedBaseName?: string;
}

export function usePersistence(cfg: PersistenceConfig) {
  const {
    diagramType, participants, messages, fragments, participantGroups,
    nodes, edges, viewport, layers, groups, pages, activePage, graphs, activeGraphId, catalog,
    setDiagramType, clearDiagram, loadDiagram, setJsonError, jsonText,
    suggestedBaseName,
  } = cfg;

  const fileHandleRef = useRef<FileSystemFileHandle | null>(null);
  const suggestedStem = sanitizeFileStem(suggestedBaseName ?? 'diagram');
  const filePickerOpts = {
    types: [{ description: 'Velostruct Diagram', accept: { 'application/json': ['.velostruct', '.velostruct', '.json'] } }],
    suggestedName: `${suggestedStem}.velostruct`,
  };

  const getJson = () => saveToJSON({
    diagramType, participants, messages, fragments, participantGroups,
    nodes, edges, viewport, layers, groups, graphs, activeGraphId, pages, activePage, catalog,
  });

  const handleNewFlow = () => {
    if (nodes.length > 0 || edges.length > 0 || groups.length > 0 || (participants && participants.length > 0)) {
      if (!window.confirm('Clear and create a new Flow Diagram?')) return;
    }
    snapshotFromStore();
    clearDiagram();
    fileHandleRef.current = null;
    setDiagramType('flow' as DiagramType);
  };

  const handleNewSequence = () => {
    if (nodes.length > 0 || edges.length > 0 || groups.length > 0 || (participants && participants.length > 0)) {
      if (!window.confirm('Clear and create a new Sequence Diagram?')) return;
    }
    snapshotFromStore();
    clearDiagram();
    fileHandleRef.current = null;
    setDiagramType('sequence' as DiagramType);
  };

  const writeToHandle = async (handle: FileSystemFileHandle, json: string) => {
    const writable = await handle.createWritable();
    await writable.write(json);
    await writable.close();
  };

  const downloadFallback = (json: string) => {
    const blob = new Blob([json], { type: 'application/json' });
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `${suggestedStem}.velostruct`,
    });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleSave = async () => {
    const json = getJson();
    if (!window.showSaveFilePicker) { downloadFallback(json); return; }
    try {
      if (fileHandleRef.current) {
        await writeToHandle(fileHandleRef.current, json);
      } else {
        const handle = await window.showSaveFilePicker({ ...filePickerOpts });
        fileHandleRef.current = handle;
        await writeToHandle(handle, json);
      }
    } catch (e: unknown) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) downloadFallback(json);
    }
  };

  const handleSaveAs = async () => {
    const json = getJson();
    if (!window.showSaveFilePicker) { downloadFallback(json); return; }
    try {
      const handle = await window.showSaveFilePicker({ ...filePickerOpts });
      fileHandleRef.current = handle;
      await writeToHandle(handle, json);
    } catch (e: unknown) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) downloadFallback(json);
    }
  };

  const handleLoad = async (e?: React.ChangeEvent<HTMLInputElement>) => {
    if (!e && window.showOpenFilePicker) {
      try {
        const [handle] = await window.showOpenFilePicker({
          types: [{ description: 'Velostruct Diagram', accept: { 'application/json': ['.velostruct', '.velostruct', '.json'] } }],
          multiple: false,
        });
        const file = await handle.getFile();
        const text = await file.text();
        loadDiagram(loadFromJSON(text));
        fileHandleRef.current = handle;
        setJsonError(null);
        useUIStore.getState().revealHeader();
      } catch (err: unknown) {
        if (!(err instanceof DOMException && err.name === 'AbortError')) alert(`Failed to load: ${err instanceof Error ? err.message : 'Invalid file'}`);
      }
      return;
    }

    const file = e?.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        loadDiagram(loadFromJSON(ev.target?.result as string));
        fileHandleRef.current = null;
        setJsonError(null);
        useUIStore.getState().revealHeader();
      } catch (err) {
        alert(`Failed to load: ${err instanceof Error ? err.message : 'Invalid file'}`);
      }
    };
    reader.readAsText(file);
    if (e) e.target.value = '';
  };

  const handleApplyJson = () => {
    try {
      snapshotFromStore();
      loadDiagram(loadFromJSON(jsonText));
      setJsonError(null);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  };

  return { handleNewFlow, handleNewSequence, handleSave, handleSaveAs, handleLoad, handleApplyJson, fileHandleRef };
}
